import { useTranslation } from 'react-i18next';
import { balanceCents, paidCents, scheduledCents, undatedCents, type Vendor } from '@guabi/shared';
import { money } from '@/lib/format';

/**
 * What the schedule adds up to.
 *
 * Every one of these numbers already existed in `vendors.ts` and was computed
 * for the budget rollup; none of them was ever shown next to the instalments
 * they come from. So the one place you are actually thinking about money for
 * this supplier was the one place that made you add it up yourself.
 *
 * **Paid and still-to-pay are measured against different things, deliberately.**
 * Paid is the sum of the instalments ticked off. Still-to-pay is the quote minus
 * what is paid — not the sum of unticked instalments — because a schedule that
 * does not add up to the quote is the normal case: the last payment usually has
 * no date, and often no row, until the week before.
 *
 * That difference is why the bar underneath only appears when there is a quote,
 * and why a schedule that disagrees with the quote says so rather than quietly
 * picking one of the two numbers.
 */
export function PaymentTotals({ vendor, currency }: { vendor: Vendor; currency: string }) {
  const { t } = useTranslation();
  const show = (cents: number) => money(cents, vendor.currency ?? currency);

  const paid = paidCents(vendor);
  const scheduled = scheduledCents(vendor);
  const balance = balanceCents(vendor);
  const undated = undatedCents(vendor);
  const quote = vendor.quoteCents;

  if (scheduled === 0 && paid === 0) return null;

  // Only meaningful against a quote, and only when the schedule disagrees with
  // it by more than rounding — otherwise it is a line saying "these agree".
  const gap = quote === undefined ? 0 : scheduled - quote;
  const pct = quote === undefined || quote === 0 ? 0 : Math.min(100, (paid / quote) * 100);

  return (
    <div className="flex flex-col gap-2 rounded border border-line bg-cream/40 p-3">
      <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1">
        <span className="flex items-baseline gap-2">
          <span className="text-caption text-leaf-soft">{t('vendors.totalPaid')}</span>
          <span className="tabular text-body font-medium">{show(paid)}</span>
        </span>
        {balance !== undefined && (
          <span className="flex items-baseline gap-2">
            <span className="text-caption text-leaf-soft">{t('vendors.totalLeft')}</span>
            <span className="tabular text-body font-medium">{show(balance)}</span>
          </span>
        )}
        <span className="flex items-baseline gap-2">
          <span className="text-caption text-leaf-soft">{t('vendors.totalScheduled')}</span>
          <span className="tabular text-body">{show(scheduled)}</span>
        </span>
      </div>

      {/* Against the quote, because that is the commitment. No bar without one:
          a progress bar with no total is a decoration. */}
      {quote !== undefined && quote > 0 && (
        <div
          className="h-1.5 w-full overflow-hidden rounded-pill bg-line"
          role="img"
          aria-label={t('vendors.paidOfQuote', { paid: show(paid), quote: show(quote) })}
        >
          <div className="h-full rounded-pill bg-leaf" style={{ width: `${String(pct)}%` }} />
        </div>
      )}

      {/* Two things worth saying out loud rather than leaving to arithmetic. */}
      {undated > 0 && (
        <p className="text-caption text-leaf-soft">
          {t('vendors.undatedTotal', { amount: show(undated) })}
        </p>
      )}
      {quote !== undefined && gap !== 0 && (
        <p className="text-caption text-leaf-soft">
          {gap > 0
            ? t('vendors.scheduleOver', { amount: show(gap) })
            : t('vendors.scheduleUnder', { amount: show(-gap) })}
        </p>
      )}
    </div>
  );
}
